import AddIcon from '@mui/icons-material/Add';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { Button, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTranslation } from '../../../contexts/translation';
import { convertValueByType } from '../../../utils/core/functions/convertValueByType';
import { StyledTableRow } from './dataTable.styles';
import { CellItem, HeaderCell } from './dataTable.types';

const useStyles: any = makeStyles({
  table: {
    minWidth: 450,
  },
});

interface Props {
  headerCells: HeaderCell[];
  items: CellItem[];
  onCreateClick?: () => void;
  onEditClick?: (id: number) => void;
  onDeleteClick?: (id: number) => void;
  onRowDoubleClick?: (id: number) => void;
  onDownloadClick?: (id: number, filename: string) => void;
  isReadOnly?: boolean;
}

export function DataTable({
  headerCells,
  items,
  onRowDoubleClick,
  onCreateClick,
  onEditClick,
  onDeleteClick,
  onDownloadClick,
  isReadOnly,
}: Props) {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <Table className={classes.table} aria-label="simple table">
      <TableHead>
        <TableRow>
          {headerCells.map((item: HeaderCell) => {
            return (
              <TableCell key={item.key} align={!item.align ? 'left' : item.align}>
                {item.title}
              </TableCell>
            );
          })}
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item: CellItem) => (
          <StyledTableRow
            key={item.id}
            onDoubleClick={() => (onRowDoubleClick ? onRowDoubleClick(item.id) : null)}
          >
            {headerCells.map((header: HeaderCell) => {
              return (
                <TableCell key={header.key}>
                  {header.cellRenderer
                    ? header.cellRenderer(item)
                    : convertValueByType(
                        item[header.key],
                        !header.dataType ? 'string' : header.dataType,
                      )}
                </TableCell>
              );
            })}
            <TableCell align={'right'}>
              {onEditClick && (
                <Tooltip title={t('general.action-buttons.detail')}>
                  <Button onClick={() => onEditClick(item.id)}>
                    {!isReadOnly && <EditIcon />}
                    {isReadOnly && <RemoveRedEyeOutlinedIcon />}
                  </Button>
                </Tooltip>
              )}
              {onDeleteClick && !isReadOnly && (
                <Tooltip title={t('general.action-buttons.delete')}>
                  <Button onClick={() => onDeleteClick(item.id)}>
                    <DeleteIcon />
                  </Button>
                </Tooltip>
              )}
              {onDownloadClick && (
                <Tooltip title={t('general.action-buttons.download')}>
                  <Button
                    onClick={() => {
                      // @ts-ignore
                      onDownloadClick(item.id, item.name);
                    }}
                  >
                    <ArrowCircleDownIcon />
                  </Button>
                </Tooltip>
              )}
            </TableCell>
          </StyledTableRow>
        ))}
        {onCreateClick && !isReadOnly && (
          <StyledTableRow>
            <TableCell colSpan={headerCells.length + 1}>
              <Tooltip title={t('general.action-buttons.new')}>
                <Button onClick={onCreateClick}>
                  <AddIcon />
                </Button>
              </Tooltip>
            </TableCell>
          </StyledTableRow>
        )}
      </TableBody>
    </Table>
  );
}
